import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, countOf, hasStrokeDraw } from '../skillUsage';

export const hubAndSpokesSkill: CodeoSkill = {
    name: SKILL_NAMES.hubAndSpokes,
    kind: 'codeo',
    description:
        'Draw connectors outward from a center to satellite nodes arranged around it — for one ' +
        'thing that everything else plugs into: a platform, an integration story, a team, a source ' +
        'that feeds many destinations.',
    documentUsesSkill: check(
        (script, markup) => hasStrokeDraw(script) && countOf(markup, /<line\b|<path\b/g) >= 2,
    ),
    instructions: `# Hub and Spokes

A center element establishes itself, then connector lines draw outward to labelled nodes arranged around it, each node arriving as its line reaches it. The claim is structural: *this* is the middle, and those things attach to it.

The technique is a dash-drawn SVG connector layer under absolutely positioned nodes, with node entrances offset to land as their line completes.

## Markup

Place nodes on a circle you compute once, so the arrangement is exact rather than eyeballed. The connector layer sits behind the nodes and spans the whole stage.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <div id="hub-stage" style="position: relative; width: 1000px; height: 800px;">
    <svg id="spokes" class="overflow-visible" viewBox="0 0 1000 800" style="position: absolute; inset: 0; width: 1000px; height: 800px;">
      <!-- one <line> per node, all starting at the hub center -->
      <line class="spoke" x1="500" y1="400" x2="500" y2="130" stroke="var(--l5-color-2, #38bdf8)" stroke-width="3" stroke-linecap="round" />
      <line class="spoke" x1="500" y1="400" x2="734" y2="535" stroke="var(--l5-color-2, #38bdf8)" stroke-width="3" stroke-linecap="round" />
      <line class="spoke" x1="500" y1="400" x2="266" y2="535" stroke="var(--l5-color-2, #38bdf8)" stroke-width="3" stroke-linecap="round" />
    </svg>
    <div id="hub" class="flex items-center justify-center" style="position: absolute; left: 500px; top: 400px; margin-left: -130px; margin-top: -130px; width: 260px; height: 260px; border-radius: 50%; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 42px; font-weight: 800; color: var(--l5-color-1, #101820);">Lumen5</div>
    <div class="node flex items-center justify-center" style="position: absolute; left: 500px; top: 130px; margin-left: -110px; margin-top: -44px; width: 220px; height: 88px; border-radius: 16px; background: var(--l5-color-1, #101820); border: 2px solid var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 30px; font-weight: 700; color: var(--l5-color-0, #ffffff);">Slack</div>
    <!-- remaining nodes -->
  </div>
</div>
\`\`\`

Center each node with negative margins rather than \`transform: translate(-50%, -50%)\` — the nodes are scale-tweened, and a CSS transform on a tweened element gets replaced.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const spokeEls = document.querySelectorAll('.spoke');
const HUB_IN = 0.15;
const SPOKE_DRAW = 0.45;
const SPOKE_STEP = 0.22;

spokeEls.forEach((spoke) => {
    const length = Math.ceil(spoke.getTotalLength()) + 2;
    gsap.set(spoke, { strokeDasharray: length, strokeDashoffset: length + 1 });
});
gsap.set('#hub', { scale: 0.7, opacity: 0 });
gsap.set('.node', { scale: 0.8, opacity: 0 });

tl.to('#hub', { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.4)' }, HUB_IN);

spokeEls.forEach((spoke, index) => {
    const at = HUB_IN + 0.45 + index * SPOKE_STEP;
    tl.to(spoke, { strokeDashoffset: 0, duration: SPOKE_DRAW, ease: 'power2.inOut' }, at);
    tl.to(
        document.querySelectorAll('.node')[index],
        { scale: 1, opacity: 1, duration: 0.36, ease: 'power3.out' },
        at + SPOKE_DRAW - 0.12,
    );
});

window.__timelines = { main: tl };
\`\`\`

The node lands slightly *before* its line finishes, so the line appears to deliver it rather than to arrive at an object already sitting there.

## Adapt

- **Node count**: three to six. Compute positions as \`cx + r * Math.cos(angle)\`, \`cy + r * Math.sin(angle)\` at author time and write the resulting numbers into the markup — don't compute them in the composition unless the count is dynamic.
- **Inward direction**: reverse the story (many sources feeding one destination) by drawing each line from the node toward the hub — set the dash offset to negative length instead, and land the node *first*.
- **Curved spokes**: swap \`<line>\` for \`<path>\` with a quadratic control point pulled toward the center; \`getTotalLength\` works the same.
- **Asymmetric arrangement**: a perfect circle reads as a stock diagram. Pulling one node further out, or clustering three on one side, reads as a real relationship.
- **Emphasis pass**: after everything lands, pulse one spoke and its node with the accent color to name the one that matters.

## Pitfalls

- **Every selector in the timeline has to exist in the markup.** If you add a heading or a subline to the composition, add it to *both* — a tween on an id you never wrote is silent, so the copy simply never appears and nothing reports it. Read the markup and the timeline side by side before finishing.
- \`getTotalLength()\` must be read from the live element after layout; it is why the dash setup runs in a loop rather than being hardcoded. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- Nodes and hub are \`scale\`-tweened, so neither may carry a CSS \`transform\`. Center with negative margins.
- The SVG needs \`overflow: visible\` or spokes reaching past the viewBox get clipped; a node sitting on the stage edge also wants \`data-l5-allow-offscreen\`.
- Don't animate the spokes and let the nodes simply fade in together — the offset between line and node is the entire choreography.
- A hub diagram with more than six nodes stops being readable as structure and becomes a starburst. Group them or cut them.
- Once assembled the frame is completely still, which is the failure mode this layout invites. Give the whole stage a slow drift or push — see \`${SKILL_NAMES.ambientFloatLoop}\` and \`${SKILL_NAMES.cinematicCameraMove}\`.
- Don't label every node *and* narrate every node. The graphic shows the structure; the voiceover carries the sentence.`,
};
